import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Space, message, Popconfirm, Card, Tabs, Row, Col, Select, Upload, Divider, Tooltip, ColorPicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DragOutlined, CloseOutlined, UploadOutlined, BgColorsOutlined, FontSizeOutlined, AlignCenterOutlined, AlignRightOutlined, BuildOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ApiClient from '../helpers/ApiClient';
import GrapesJSBuilder from './GrapesJSBuilder';
import './LandingPageBuilder.scss';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const SECTION_TYPES = {
  HERO: 'hero',
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  GALLERY: 'gallery',
  TESTIMONIALS: 'testimonials',
  PACKAGES: 'packages',
  CONTACT_FORM: 'contact_form',
  WHY_CHOOSE_US: 'why_choose_us'
};

const SectionEditor = ({ section, onUpdate, onDelete }) => {
  const [activeTab, setActiveTab] = useState('content');
  
  const updateSection = (field, value) => {
    onUpdate({ ...section, [field]: value });
  };

  const updateStyle = (field, value) => {
    const styles = section.styles || {};
    onUpdate({ ...section, styles: { ...styles, [field]: value } });
  };

  const renderContentFields = () => {
    switch (section.type) {
      case SECTION_TYPES.HERO:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Hero Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Subtitle">
              <TextArea
                placeholder="Hero Subtitle"
                value={section.subtitle || ''}
                onChange={(e) => updateSection('subtitle', e.target.value)}
                rows={2}
              />
            </Form.Item>
            <Form.Item label="Background Image">
              <Input
                placeholder="Background Image URL"
                value={section.backgroundImage || ''}
                onChange={(e) => updateSection('backgroundImage', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Show Booking Form">
              <Switch
                checked={section.showBookingForm || false}
                onChange={(checked) => updateSection('showBookingForm', checked)}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.TEXT:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Section Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Content">
              <TextArea
                placeholder="Content"
                value={section.content || ''}
                onChange={(e) => updateSection('content', e.target.value)}
                rows={4}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.IMAGE:
        return (
          <>
            <Form.Item label="Image URL">
              <Input
                placeholder="Image URL"
                value={section.imageUrl || ''}
                onChange={(e) => updateSection('imageUrl', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Alt Text">
              <Input
                placeholder="Alt Text"
                value={section.altText || ''}
                onChange={(e) => updateSection('altText', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Caption">
              <Input
                placeholder="Caption (optional)"
                value={section.caption || ''}
                onChange={(e) => updateSection('caption', e.target.value)}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.VIDEO:
        return (
          <>
            <Form.Item label="Video URL">
              <Input
                placeholder="Video URL (YouTube/Vimeo)"
                value={section.videoUrl || ''}
                onChange={(e) => updateSection('videoUrl', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Title">
              <Input
                placeholder="Video Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.GALLERY:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Gallery Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Images">
              <TextArea
                placeholder="Image URLs (one per line)"
                value={section.images?.join('\n') || ''}
                onChange={(e) => updateSection('images', e.target.value.split('\n').filter(url => url.trim()))}
                rows={4}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.TESTIMONIALS:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Section Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Testimonials">
              <TextArea
                placeholder="Testimonial Image URLs (one per line)"
                value={section.testimonials?.join('\n') || ''}
                onChange={(e) => updateSection('testimonials', e.target.value.split('\n').filter(url => url.trim()))}
                rows={4}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.WHY_CHOOSE_US:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Section Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Description">
              <TextArea
                placeholder="Description"
                value={section.description || ''}
                onChange={(e) => updateSection('description', e.target.value)}
                rows={3}
              />
            </Form.Item>
            <Form.Item label="Image">
              <Input
                placeholder="Image URL"
                value={section.image || ''}
                onChange={(e) => updateSection('image', e.target.value)}
              />
            </Form.Item>
          </>
        );
      case SECTION_TYPES.CONTACT_FORM:
        return (
          <>
            <Form.Item label="Title">
              <Input
                placeholder="Form Title"
                value={section.title || ''}
                onChange={(e) => updateSection('title', e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Description">
              <TextArea
                placeholder="Form Description"
                value={section.description || ''}
                onChange={(e) => updateSection('description', e.target.value)}
                rows={2}
              />
            </Form.Item>
          </>
        );
      default:
        return <div>Section type not supported</div>;
    }
  };

  const renderStyleFields = () => (
    <>
      <Form.Item label="Background Color">
        <Input
          placeholder="#ffffff or transparent"
          value={section.styles?.backgroundColor || ''}
          onChange={(e) => updateStyle('backgroundColor', e.target.value)}
        />
      </Form.Item>
      <Form.Item label="Text Color">
        <Input
          placeholder="#000000"
          value={section.styles?.color || ''}
          onChange={(e) => updateStyle('color', e.target.value)}
        />
      </Form.Item>
      <Form.Item label="Padding">
        <Input
          placeholder="20px 40px"
          value={section.styles?.padding || ''}
          onChange={(e) => updateStyle('padding', e.target.value)}
        />
      </Form.Item>
      <Form.Item label="Text Align">
        <Select
          placeholder="Select alignment"
          value={section.styles?.textAlign || 'left'}
          onChange={(value) => updateStyle('textAlign', value)}
        >
          <Option value="left">Left</Option>
          <Option value="center">Center</Option>
          <Option value="right">Right</Option>
        </Select>
      </Form.Item>
      <Form.Item label="Font Size">
        <Input
          placeholder="16px"
          value={section.styles?.fontSize || ''}
          onChange={(e) => updateStyle('fontSize', e.target.value)}
        />
      </Form.Item>
    </>
  );

  return (
    <Card
      className="section-editor"
      style={{
        marginBottom: 16,
        border: '2px solid #e8e8e8',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <DragOutlined style={{ cursor: 'grab', fontSize: '16px', color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {section.type.replace('_', ' ').toUpperCase()}
          </span>
        </Space>
        <Tooltip title="Delete Section">
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => onDelete(section.id)}
            style={{ color: '#ff4d4f' }}
          />
        </Tooltip>
      </div>
      
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        items={[
          {
            key: 'content',
            label: 'Content',
            children: <Form layout="vertical" size="small">{renderContentFields()}</Form>
          },
          {
            key: 'style',
            label: 'Style',
            children: <Form layout="vertical" size="small">{renderStyleFields()}</Form>
          }
        ]}
      />
    </Card>
  );
};

const LandingPages = () => {
  const [landingPages, setLandingPages] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBuilderVisible, setIsBuilderVisible] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [nextSectionId, setNextSectionId] = useState(1);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    if (title && !editingPage) {
      const slug = generateSlug(title);
      form.setFieldsValue({ slug });
    }
  };

  useEffect(() => {
    fetchLandingPages();
  }, []);

  const fetchLandingPages = async () => {
    try {
      console.log('Fetching from:', process.env.REACT_APP_BASE_URL + 'admin/landing-pages');
      const response = await ApiClient.get('admin/landing-pages');
      console.log('Full API response:', response);
      
      if (response) {
        // Check different possible response structures
        let data = [];
        if (response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response)) {
          data = response;
        }
        
        console.log('Processed data:', data);
        setLandingPages(data);
      } else {
        console.log('No response received');
        setLandingPages([]);
      }
    } catch (error) {
      console.error('Fetch error details:', error);
      message.error(`Failed to fetch landing pages: ${error.message}`);
      setLandingPages([]);
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: nextSectionId,
      type,
      order: sections.length
    };
    setSections([...sections, newSection]);
    setNextSectionId(nextSectionId + 1);
  };

  const updateSection = (updatedSection) => {
    setSections(sections.map(section => 
      section.id === updatedSection.id ? updatedSection : section
    ));
  };

  const deleteSection = (sectionId) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setSections(updatedItems);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      console.log('Form values before processing:', values);
      console.log('Sections:', sections);
      
      const payload = {
        ...values,
        sections: sections.sort((a, b) => a.order - b.order)
      };
      
      console.log('Payload being sent:', payload);

      let response;
      if (editingPage) {
        response = await ApiClient.put(`admin/landing-pages/${editingPage.id}`, {}, payload);
      } else {
        response = await ApiClient.post('admin/landing-pages', payload);
      }
      
      console.log('API response:', response);
      
      if (response.status === 200 || response.status === 201) {
        message.success(editingPage ? 'Landing page updated successfully' : 'Landing page created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setEditingPage(null);
        setSections([]);
        setNextSectionId(1);
        fetchLandingPages();
      }
    } catch (error) {
      console.error('Save error:', error);
      message.error('Failed to save landing page');
    } finally {
      setLoading(false);
    }
  };

  const handleBuilderSave = async (payload) => {
    let response;
    if (editingPage) {
      response = await ApiClient.put(`admin/landing-pages/${editingPage.id}`, {}, payload);
    } else {
      response = await ApiClient.post('admin/landing-pages', payload);
    }
    
    if (response.status === 200 || response.status === 201) {
      setIsBuilderVisible(false);
      setEditingPage(null);
      fetchLandingPages();
    }
  };

  const openVisualBuilder = (page = null) => {
    setEditingPage(page);
    setIsBuilderVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPage(record);
    console.log('Editing record:', record);
    
    const formData = {
      title: record.title,
      slug: record.slug,
      isActive: record.isActive,
      metaTitle: record.metaTitle,
      metaDescription: record.metaDescription,
      keywords: record.keywords,
      ogTitle: record.ogTitle,
      ogImage: record.ogImage,
      canonicalUrl: record.canonicalUrl
    };
    
    // Load sections if they exist
    if (record.sections && Array.isArray(record.sections)) {
      setSections(record.sections.map((section, index) => ({
        ...section,
        id: index + 1,
        order: index
      })));
      setNextSectionId(record.sections.length + 1);
    } else {
      setSections([]);
      setNextSectionId(1);
    }
    
    console.log('Form data for editing:', formData);
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await ApiClient.delete(`admin/landing-pages/${id}`);
      if (response.status === 200) {
        message.success('Landing page deleted successfully');
        fetchLandingPages();
      }
    } catch (error) {
      message.error('Failed to delete landing page');
    }
  };

  const toggleStatus = async (id, isActive) => {
    try {
      const response = await ApiClient.patch(`admin/landing-pages/${id}/status`, { isActive });
      if (response.status === 200) {
        message.success(`Landing page ${isActive ? 'activated' : 'deactivated'}`);
        fetchLandingPages();
      }
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug) => <code>/{slug}</code>
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => toggleStatus(record.id, checked)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => window.open(`/${record.slug}`, '_blank')}
            title="Preview"
          />
          <Button icon={<EditOutlined />} onClick={() => openVisualBuilder(record)} title="Edit Page" />
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="container-fluid">
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<BuildOutlined />}
            onClick={() => openVisualBuilder()}
          >
            Create New Page
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={landingPages}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />

        <Modal
          title={editingPage ? 'Edit Landing Page' : 'Create Landing Page'}
          visible={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingPage(null);
            setSections([]);
            setNextSectionId(1);
          }}
          footer={null}
          width={1200}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Tabs defaultActiveKey="1">
              <TabPane tab="Basic Info" key="1">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label="Page Title"
                      rules={[{ required: true }]}
                    >
                      <Input 
                        placeholder="Japan Tour Packages" 
                        onChange={handleTitleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="slug"
                      label="URL Slug"
                      rules={[
                        { required: true },
                        { pattern: /^[a-z0-9-]+$/, message: 'Only lowercase letters, numbers, and hyphens allowed' }
                      ]}
                      extra="URL will be: yoursite.com/[slug]"
                    >
                      <Input 
                        placeholder="japan-tour-packages" 
                        addonBefore="/"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="isActive"
                  label="Active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </TabPane>

              <TabPane tab="SEO Settings" key="2">
                <Form.Item
                  name="metaTitle"
                  label="Meta Title"
                  extra="Recommended: 50-60 characters"
                >
                  <Input placeholder="Best Japan Tour Packages | Your Travel Company" maxLength={60} showCount />
                </Form.Item>

                <Form.Item
                  name="metaDescription"
                  label="Meta Description"
                  extra="Recommended: 150-160 characters"
                >
                  <TextArea placeholder="Discover amazing Japan tour packages with best prices. Book your dream Japan vacation today!" rows={3} maxLength={160} showCount />
                </Form.Item>

                <Form.Item
                  name="keywords"
                  label="SEO Keywords"
                  extra="Separate keywords with commas"
                >
                  <TextArea placeholder="japan tour, japan packages, tokyo travel, japan vacation, cherry blossom tour" rows={2} />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="ogTitle"
                      label="Open Graph Title"
                      extra="For social media sharing"
                    >
                      <Input placeholder="Amazing Japan Tour Packages" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="ogImage"
                      label="Open Graph Image URL"
                      extra="Recommended: 1200x630px"
                    >
                      <Input placeholder="https://example.com/og-image.jpg" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="canonicalUrl"
                  label="Canonical URL"
                  extra="Optional: Specify canonical URL to avoid duplicate content"
                >
                  <Input placeholder="https://yoursite.com/japan-tour-packages" />
                </Form.Item>
              </TabPane>

              <TabPane tab="🎨 Page Builder" key="3">
                <div className="elementor-panel">
                  <div className="panel-header">
                    📦 Add Sections
                  </div>
                  <Row gutter={[8, 8]} className="section-type-grid">
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.HERO)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        🎯<br/>Hero
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.TEXT)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        📝<br/>Text
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.IMAGE)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        🖼️<br/>Image
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.VIDEO)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        🎥<br/>Video
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.GALLERY)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        🖼️<br/>Gallery
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.TESTIMONIALS)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        💬<br/>Reviews
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.WHY_CHOOSE_US)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        ⭐<br/>Features
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button 
                        onClick={() => addSection(SECTION_TYPES.CONTACT_FORM)} 
                        block
                        style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                      >
                        📞<br/>Contact
                      </Button>
                    </Col>
                  </Row>
                </div>

                <div className="elementor-panel" style={{ marginTop: 24 }}>
                  <div className="panel-header">
                    🔄 Page Structure
                  </div>

                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="sections">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="drag-drop-area">
                        {sections.map((section, index) => (
                          <Draggable key={section.id} draggableId={section.id.toString()} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <SectionEditor
                                  section={section}
                                  onUpdate={updateSection}
                                  onDelete={deleteSection}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                          {provided.placeholder}
                          
                          {sections.length === 0 && (
                            <div className="empty-state">
                              <div className="empty-icon">🎨</div>
                              <h3>Start Building Your Page</h3>
                              <p>Drag sections here or click the buttons above to add content blocks</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </TabPane>
            </Tabs>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingPage ? 'Update' : 'Create'}
                </Button>
                <Button onClick={() => {
                  setIsModalVisible(false);
                  setSections([]);
                  setNextSectionId(1);
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <GrapesJSBuilder
          visible={isBuilderVisible}
          onClose={() => {
            setIsBuilderVisible(false);
            setEditingPage(null);
          }}
          editingPage={editingPage}
          onSave={handleBuilderSave}
        />
      </Card>
    </div>
  );
};

export default LandingPages;